import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { ApiClient, CreatedContactResponse, NewContactRequest, SendPaymentRequestRequest, SendPaymentRequestResponse } from '../sdk/sdk';
import { toSendPaymentRequestError } from './send-payment-request-error';

@Injectable({
  providedIn: 'root',
})
export class PaymentRequestService {
  private readonly sdk = inject(ApiClient);

  // amount null/omitted sends no `amount` at all, which the server reads as "bill the full balance
  // due today" — byte-identical to the request this method produced before split payments.
  sendForJob(jobId: string, contactId: string, amount: number | null = null): Observable<SendPaymentRequestResponse> {
    const body = new SendPaymentRequestRequest({ contactId, amount: amount ?? undefined });
    return this.sdk.sendPaymentRequest(jobId, body).pipe(
      catchError((err) => throwError(() => toSendPaymentRequestError(err)))
    );
  }

  createOpportunityContact(
    opportunityId: string,
    payload: { firstName: string; lastName: string; phoneNumber: string; email: string | null },
  ): Observable<CreatedContactResponse> {
    const body = new NewContactRequest({
      firstName: payload.firstName,
      lastName: payload.lastName,
      phoneNumber: payload.phoneNumber,
      email: payload.email ?? undefined,
    });
    return this.sdk.createOpportunityContact(opportunityId, body);
  }

  getStatementUrlForJob(jobId: string): Observable<string | null> {
    return this.sdk.getCustomerStatementUrl(jobId).pipe(
      map((response) => response.url ?? null),
      catchError((err) => {
        // 404 is the only expected error: the job has no PublicKey yet (or the
        // payments feature is gated off server-side). Treat as "no statement
        // available" so callers can show an info message. Anything else (auth,
        // network, 5xx) is a real failure and must propagate so the UI can
        // surface it instead of pretending the statement just doesn't exist.
        if (err?.status === 404) return of(null);
        return throwError(() => err);
      })
    );
  }
}
